import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { lazy, useState, useEffect } from 'react'
import { useAuth } from '../providers/AuthProvider'
import { PlatformAdminGuard, OrganizationAdminGuard, VerificationGuard } from './guards'
import { getPostLoginRedirectUrl } from '@/features/auth/services/postLoginRedirect'

// Layouts
const PublicLayout = lazy(() => import('./layouts/PublicLayout'))
const AuthLayout = lazy(() => import('./layouts/AuthLayout'))
const AdminLayout = lazy(() => import('./layouts/AdminLayout'))
const PlatformLayout = lazy(() => import('./layouts/PlatformLayout'))
const PortalLayout = lazy(() => import('./layouts/PortalLayout'))

// Public Pages
const LandingPage = lazy(() => import('@/features/auth/pages/LandingPage'))
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'))
const SignupPage = lazy(() => import('@/features/auth/pages/SignupPage'))
const PricingPage = lazy(() => import('@/features/billing/pages/PricingPage'))

// Platform Pages
const PlatformDashboardPage = lazy(() => import('@/features/platform/pages/DashboardPage'))
const OrganizationsPage = lazy(() => import('@/features/platform/pages/OrganizationsPage'))
const OrganizationDetailPage = lazy(() => import('@/features/platform/pages/OrganizationDetailPage'))
const UsersPage = lazy(() => import('@/features/platform/pages/UsersPage'))
const UserDetailPage = lazy(() => import('@/features/platform/pages/UserDetailPage'))
const ImamsPage = lazy(() => import('@/features/platform/pages/ImamsPage'))
const PlansPage = lazy(() => import('@/features/platform/pages/PlansPage'))
const DiscountCodesPage = lazy(() => import('@/features/platform/pages/DiscountCodesPage'))
const AnalyticsPage = lazy(() => import('@/features/platform/pages/AnalyticsPage'))
const PlatformSettingsPage = lazy(() => import('@/features/platform/pages/SettingsPage'))

// Auth Pages (Approval Flow)
const PendingApprovalPage = lazy(() => import('@/features/auth/pages/PendingApprovalPage'))
const NoOrganizationPage = lazy(() => import('@/features/auth/pages/NoOrganizationPage'))

// Password Reset Pages
const PasswordResetRequestPage = lazy(() => import('@/features/auth/pages/PasswordResetRequestPage'))
const PasswordResetConfirmPage = lazy(() => import('@/features/auth/pages/PasswordResetConfirmPage'))

// Email Verification Pages
const EmailVerificationPage = lazy(() => import('@/features/auth/pages/EmailVerificationPage'))
const EmailVerificationPendingPage = lazy(() => import('@/features/auth/pages/EmailVerificationPendingPage'))

// User Dashboard
const UserDashboardPage = lazy(() => import('@/features/user/pages/UserDashboardPage'))

// Admin Pages
const AdminDashboardPage = lazy(() => import('@/features/admin/pages/DashboardPage'))
const PeoplePage = lazy(() => import('@/features/members/pages/PeoplePage'))
const MemberDetailPage = lazy(() => import('@/features/members/pages/MemberDetailPage'))
const HouseholdsPage = lazy(() => import('@/features/households/pages/HouseholdsPage'))
const DonorsPage = lazy(() => import('@/features/donations/pages/DonorsPage'))
const TaxReceiptsPage = lazy(() => import('@/features/donations/pages/TaxReceiptsPage'))
const ExpensesPage = lazy(() => import('@/features/expenses/pages/ExpensesPage'))
const EducationPage = lazy(() => import('@/features/education/pages/EducationPage'))
const ClassEditPage = lazy(() => import('@/features/education/pages/ClassEditPage'))
const AdminCasesPage = lazy(() => import('@/features/cases/pages/CasesPage'))
const UmrahPage = lazy(() => import('@/features/umrah/pages/UmrahPage'))
const QurbaniPage = lazy(() => import('@/features/qurbani/pages/QurbaniPage'))
const AnnouncementsPage = lazy(() => import('@/features/announcements/pages/AnnouncementsPage'))
const IslamicServicesPage = lazy(() => import('@/features/islamic-services/pages/IslamicServicesPage'))
const BillingPage = lazy(() => import('@/features/billing/pages/BillingPage'))
const ReportsPage = lazy(() => import('@/features/reports/pages/ReportsPage'))
const KPIReportsPage = lazy(() => import('@/features/reports/pages/KPIReportsPage'))
const BankReconciliationPage = lazy(() => import('@/features/finance/pages/BankReconciliationPage'))
const FundsPage = lazy(() => import('@/features/donations/pages/FundsPage'))

// Ramadan Pages
const RamadanDashboardPage = lazy(() => import('@/features/ramadan/pages/RamadanDashboardPage'))
const ZakatCalculatorPage = lazy(() => import('@/features/ramadan/pages/ZakatCalculatorPage'))
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'))
const PermissionsPage = lazy(() => import('@/features/permissions/pages/PermissionsPage'))

// Events Pages
const EventsPage = lazy(() => import('@/features/events/pages/EventsPage'))
const EventDetailPage = lazy(() => import('@/features/events/pages/EventDetailPage'))

// Portal Pages
const PortalDashboardPage = lazy(() => import('@/features/portal/pages/DashboardPage'))
const ProfilePage = lazy(() => import('@/features/portal/pages/ProfilePage'))
const RecurringDonationsPage = lazy(() => import('@/features/portal/pages/RecurringDonationsPage'))
const PortalCasesPage = lazy(() => import('@/features/portal/pages/CasesPage'))
const PortalContactPage = lazy(() => import('@/features/portal/pages/ContactPage'))

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Public Route Component (redirects authenticated users intelligently)
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (user) {
    return <IntelligentRedirect />
  }

  return <>{children}</>
}

/**
 * IntelligentRedirect - Handles redirecting authenticated users to the best possible page
 * based on their permissions and organization membership.
 */
function IntelligentRedirect() {
  const { user } = useAuth()
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)

  useEffect(() => {
    async function determineRedirect() {
      if (user?.email) {
        const url = await getPostLoginRedirectUrl(user.email)
        setRedirectUrl(url)
      } else {
        setRedirectUrl('/dashboard')
      }
    }
    determineRedirect()
  }, [user])

  if (!redirectUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  return <Navigate to={redirectUrl} replace />
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
      </Route>

      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <LoginPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicOnlyRoute>
              <SignupPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicOnlyRoute>
              <PasswordResetRequestPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/reset-password/confirm"
          element={<PasswordResetConfirmPage />}
        />
      </Route>

      {/* Email Verification Routes */}
      <Route path="/verify-email" element={<EmailVerificationPage />} />
      <Route
        path="/verify-email/pending"
        element={
          <ProtectedRoute>
            <EmailVerificationPendingPage />
          </ProtectedRoute>
        }
      />

      {/* Approval Flow Routes */}
      <Route
        path="/pending-approval"
        element={
          <ProtectedRoute>
            <PendingApprovalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/no-organization"
        element={
          <ProtectedRoute>
            <NoOrganizationPage />
          </ProtectedRoute>
        }
      />

      {/* User Dashboard - The Global "My Masjids" View */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <UserDashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Platform Admin Routes */}
      <Route
        path="/platform"
        element={
          <ProtectedRoute>
            <VerificationGuard>
              <PlatformAdminGuard>
                <PlatformLayout />
              </PlatformAdminGuard>
            </VerificationGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<PlatformDashboardPage />} />
        <Route path="organizations" element={<OrganizationsPage />} />
        <Route path="organizations/:id" element={<OrganizationDetailPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="imams" element={<ImamsPage />} />
        <Route path="plans" element={<PlansPage />} />
        <Route path="discount-codes" element={<DiscountCodesPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="settings" element={<PlatformSettingsPage />} />
      </Route>

      {/* Member Portal Routes - High priority for user experience */}
      <Route
        path="/:slug/portal"
        element={
          <ProtectedRoute>
            <VerificationGuard>
              <PortalLayout />
            </VerificationGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<PortalDashboardPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="recurring-donations" element={<RecurringDonationsPage />} />
        <Route path="cases" element={<PortalCasesPage />} />
        <Route path="contact" element={<PortalContactPage />} />
        <Route path="documents" element={<Navigate to="profile?tab=donations" replace />} />
      </Route>

      {/* Organization Admin Routes */}
      <Route
        path="/:slug/admin"
        element={
          <ProtectedRoute>
            <VerificationGuard>
              <OrganizationAdminGuard>
                <AdminLayout />
              </OrganizationAdminGuard>
            </VerificationGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboardPage />} />
        <Route path="people" element={<PeoplePage />} />
        <Route path="people/:id" element={<MemberDetailPage />} />
        <Route path="households" element={<HouseholdsPage />} />
        <Route path="donors" element={<DonorsPage />} />
        <Route path="donations/tax-receipts" element={<TaxReceiptsPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="finance/reconciliation" element={<BankReconciliationPage />} />
        <Route path="donations/funds" element={<FundsPage />} />
        <Route path="education" element={<EducationPage />} />
        <Route path="education/class/:id" element={<ClassEditPage />} />
        <Route path="cases" element={<AdminCasesPage />} />
        <Route path="umrah" element={<UmrahPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="reports/kpi" element={<KPIReportsPage />} />
        <Route path="ramadan" element={<RamadanDashboardPage />} />
        <Route path="ramadan/zakat" element={<ZakatCalculatorPage />} />
        <Route path="settings" element={<SettingsPage />} />

        {/* Placeholder routes for future pages */}
        <Route path="qurbani" element={<QurbaniPage />} />
        <Route path="services" element={<IslamicServicesPage />} />
        <Route path="announcements" element={<AnnouncementsPage />} />
        <Route path="permissions" element={<PermissionsPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="events/:eventId" element={<EventDetailPage />} />
      </Route>

      {/* Dev mode redirects to help navigation - preserves subpath */}
      <Route path="/portal" element={<Navigate to="/masjid-al-amanah/portal" replace />} />
      <Route path="/portal/*" element={<PortalRedirect />} />

      {/* 404 */}
      <Route path="*" element={<div className="min-h-screen flex items-center justify-center">404 - Not Found</div>} />
    </Routes>
  )
}

/**
 * Helper component to redirect /portal/* to /masjid-al-amanah/portal/*
 * This preserves the rest of the URL path (like /portal/profile)
 */
function PortalRedirect() {
  const location = useLocation()
  // Correctly extract the subpath for paths like /portal/profile or /portal/contact
  const subpath = location.pathname.startsWith('/portal/')
    ? location.pathname.substring(8)
    : '';

  console.log('PortalRedirect: redirecting to', `/${subpath}`);
  return <Navigate to={`/masjid-al-amanah/portal/${subpath}${location.search}`} replace />
}
